import {
  type UseDisclosureProps,
  useControllableState,
  useDisclosure,
} from '@/ui';

type UseMaybeControlledDisclosure = (
  props: Partial<Pick<UseDisclosureProps, 'isOpen' | 'onOpen' | 'onClose'>>
) => {
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
};

/**
 * An implementation of Chakra's `useDisclosure` that allows for either
 * controlled or uncontrolled state.
 */
export const useMaybeControlledDisclosure: UseMaybeControlledDisclosure = ({
  isOpen: controlledIsOpen,
  onOpen: controlledOnOpen,
  onClose: controlledOnClose,
}) => {
  const {
    isOpen: uncontrolledIsOpen,
    onOpen: uncontrolledOnOpen,
    onClose: uncontrolledOnClose,
  } = useDisclosure();

  const [normalizedIsOpen, setIsOpen] = useControllableState({
    value: controlledIsOpen,
    defaultValue: uncontrolledIsOpen,
    onChange: (value) => {
      const normalizedOnOpen = controlledOnOpen ?? uncontrolledOnOpen;
      const normalizedOnClose = controlledOnClose ?? uncontrolledOnClose;

      // This hook was designed for popovers inside the entry modal.
      // setTimeout is used so that, when the popover is open and a click occurs
      // on the modal overlay, the modal sees the state of the popover when the
      // click occured and not the state when the click is eventually propagated to
      // the overlay. This lets us stop the modal from closing if the intention
      // of the overlay click was to close the popover.
      setTimeout(value ? normalizedOnOpen : normalizedOnClose, 10);
    },
  });

  return {
    isOpen: normalizedIsOpen,
    onOpen: () => setIsOpen(true),
    onClose: () => setIsOpen(false),
  };
};
